/**
 * Settings hub (ARCHITECTURE.md §8, §11).
 *
 * The landing surface: the ambient trading posture (armed / dry-run) restated in
 * full, then the three feature-gate cards (`{ ai, toss, push }`) that unlock the
 * rest of the product, plus a link into general operational settings. Gating is
 * data-driven from `/api/settings.features`; locked capabilities render a CTA
 * rather than a dead end (§8).
 */
import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { Badge } from "@richrich/ui";
import { useI18n } from "@richrich/i18n/react";
import type { FeatureFlags } from "@richrich/contracts";
import { PageShell } from "../../components/PageShell";
import { settingsQuery } from "../../lib/query";
import { credentialsQuery } from "../../features/settings/api";
import { FEATURE_GATES, type FeatureGateMeta } from "../../features/settings/catalog";

export const Route = createFileRoute("/settings/")({
  loader: async ({ context }) => {
    await Promise.all([
      context.queryClient.ensureQueryData(settingsQuery),
      context.queryClient.ensureQueryData(credentialsQuery),
    ]);
  },
  component: SettingsIndexPage,
});

function SettingsIndexPage() {
  const { t } = useI18n();
  const settings = useQuery(settingsQuery);
  const features = settings.data?.features;
  const armed = settings.data?.tradingArmed === true;
  const dryRun = settings.data?.defaultDryRun !== false;

  return (
    <PageShell
      eyebrow={t("settings.hubEyebrow")}
      title={t("nav.settings")}
      description={t("settings.hubDescription")}
      loading={settings.isLoading}
    >
      <div className="flex flex-col gap-6">
        {/* Trading posture — the §0.5 safety state, restated in full. */}
        <section
          className={
            armed
              ? "rounded-panel border border-critical/40 bg-critical/5 px-5 py-4"
              : "rounded-panel border border-hairline bg-surface px-5 py-4"
          }
        >
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="flex items-center gap-3">
              <span
                aria-hidden
                className={
                  armed
                    ? "h-2.5 w-2.5 animate-pulse rounded-full bg-critical"
                    : "h-2.5 w-2.5 rounded-full bg-muted"
                }
              />
              <div>
                <div
                  className={
                    armed
                      ? "font-mono text-xs font-semibold uppercase tracking-[0.16em] text-critical"
                      : "font-mono text-xs font-semibold uppercase tracking-[0.16em] text-muted"
                  }
                >
                  {armed ? t("nav.tradingArmed") : t("nav.disarmed")}
                </div>
                <div className="mt-0.5 text-xs text-ink-secondary">
                  {dryRun ? t("settings.dryRunDefaultOn") : t("settings.dryRunDefaultOff")}
                </div>
              </div>
            </div>
            <Link
              to="/settings/general"
              className="rounded-md border border-hairline bg-surface px-3 py-1.5 text-xs font-medium text-ink transition-colors hover:bg-elevated"
            >
              {t("settings.manageArmingDefaults")}
            </Link>
          </div>
        </section>

        {/* Feature gates. */}
        <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
          {FEATURE_GATES.map((gate) => (
            <GateCard key={gate.key} gate={gate} features={features} />
          ))}
        </div>

        {/* Agent tools — which tools each AI agent may use. */}
        <section className="rounded-panel border border-hairline bg-surface px-5 py-4">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <h2 className="text-sm font-semibold text-ink">{t("settings.toolsTitle")}</h2>
              <p className="mt-0.5 text-xs text-ink-secondary">
                {t("settings.toolsHubDescription")}
              </p>
            </div>
            <Link
              to="/settings/tools"
              className="rounded-md border border-hairline bg-surface px-3 py-1.5 text-xs font-medium text-ink transition-colors hover:bg-elevated"
            >
              {t("settings.manageAgentTools")}
            </Link>
          </div>
        </section>
      </div>
    </PageShell>
  );
}

function GateCard({
  gate,
  features,
}: {
  gate: FeatureGateMeta;
  features: FeatureFlags | undefined;
}) {
  const { t } = useI18n();
  const unlocked = features?.[gate.key] === true;
  return (
    <Link
      to={gate.to}
      className="group flex flex-col gap-3 rounded-panel border border-hairline bg-surface p-5 transition-colors hover:border-brand/40 hover:bg-elevated"
    >
      <div className="flex items-center justify-between gap-2">
        <h2 className="text-sm font-semibold text-ink">{t(gate.titleKey)}</h2>
        {unlocked ? (
          <Badge tone="good" dot>
            {t("settings.unlocked")}
          </Badge>
        ) : (
          <Badge tone="neutral" dot>
            {t("settings.locked")}
          </Badge>
        )}
      </div>
      <p className="text-xs text-ink-secondary">{t(gate.unlocksKey)}</p>
      <span className="mt-auto text-xs font-medium text-brand">
        {unlocked ? t(gate.unlockedNoteKey) : t(gate.lockedCtaKey)} →
      </span>
    </Link>
  );
}
